/**
 * Route constants and configuration for centralized route management
 *
 * This file contains all route paths, configurations, and registration utilities
 * used throughout the application, providing a single source of truth for routing.
 */

import type { RouteConfig } from '#states/RouterState.js';

import { ROUTES } from '@elara/client-core/constants';
import { generatePageTitle } from '@elara/client-core/lib/page-title';

import { AdminShell } from '#views/admin/AdminShell.jsx';
import { FlightCheckoutPage } from '#views/checkout/FlightCheckoutPage.jsx';
import { HotelCheckoutPage } from '#views/checkout/HotelCheckoutPage.jsx';
import { OnboardingPage } from '#views/onboarding/OnboardingPage.jsx';
import { AccountPage } from '#views/profiles/AccountPage.jsx';
import { FamilyPage } from '#views/profiles/FamilyPage.jsx';
import { NotificationsSettingsPage } from '#views/profiles/NotificationsSettingsPage.jsx';
import { PaymentMethodsPage } from '#views/profiles/PaymentMethodsPage.jsx';
import { ProfilePage } from '#views/profiles/ProfilePage.jsx';
import { SecurityPage } from '#views/profiles/SecurityPage.jsx';
import { SettingsPage } from '#views/profiles/SettingsPage.jsx';
import { TravelPreferencesPage } from '#views/profiles/TravelPreferencesPage.jsx';
// Import route components
import { FirebaseActionPage } from '#views/session/FirebaseActionPage.jsx';
import { ForgotPasswordPage } from '#views/session/ForgotPasswordPage.jsx';
import { LoginPage } from '#views/session/LoginPage.jsx';
import { MembershipPaymentPage } from '#views/session/MembershipPaymentPage.jsx';
import { ResetPasswordPage } from '#views/session/ResetPasswordPage.jsx';
import { SignupPage } from '#views/session/SignupPage.jsx';
import { VerifyPhonePage } from '#views/session/VerifyPhonePage.jsx';
import { SharedItineraryPage } from '#views/share/SharedItineraryPage.jsx';
import { StoryPage } from '#views/stories/StoryPage.jsx';
import { TripPage } from '#views/trip/TripPage.jsx';
import { TripsPage } from '#views/trip/TripsPage.jsx';

export const DEFAULT_TITLE = 'Elara';

/**
 * Route parameter types for type-safe navigation.
 *
 * The canonical `RouteParams`/`RoutePath`/`GetRouteParams` now live in the DOM-free
 * `@elara/client-core/states/routing-contract` (shared with `@elara/mobile`, ELARA-704). Re-exported here so web
 * call sites importing from `#routes` are unchanged.
 */
export type { GetRouteParams, RouteParams, RoutePath } from '@elara/client-core/states/routing-contract';

/**
 * Default route configurations
 * These are registered during router initialization
 *
 * Note: 404 handling is done in App.tsx by rendering PageNotFound
 * when no routes match, rather than using a wildcard route.
 */
export const DEFAULT_ROUTES: RouteConfig[] = [
  { path: ROUTES.HOME, component: TripsPage, requiresAuth: true, title: generatePageTitle('Trips') },
  { path: ROUTES.TRIP_NEW, component: TripPage, requiresAuth: true, title: generatePageTitle('New Trip') },
  { path: ROUTES.STORY, component: StoryPage, requiresAuth: true, title: generatePageTitle('Story') },
  {
    path: ROUTES.TRIP,
    component: TripPage,
    requiresAuth: true,
    title: generatePageTitle('Trip'), // Will be overridden by component with actual trip name
  },
  {
    // Trip with an explicit panel segment (e.g. /trip/:id/shortlist). Same page; the active panel is
    // read from the path. ELARA-704 replaced the legacy `?panel=` query param.
    path: ROUTES.TRIP_PANEL,
    component: TripPage,
    requiresAuth: true,
    title: generatePageTitle('Trip'),
  },
  { path: ROUTES.ACCOUNT, component: AccountPage, requiresAuth: true, title: generatePageTitle('Account') },
  { path: ROUTES.PROFILE, component: ProfilePage, requiresAuth: true, title: generatePageTitle('Profile') },
  { path: ROUTES.FAMILY, component: FamilyPage, requiresAuth: true, title: generatePageTitle('Family & Friends') },
  {
    path: ROUTES.TRAVEL_PREFERENCES,
    component: TravelPreferencesPage,
    requiresAuth: true,
    title: generatePageTitle('Travel Preferences'),
  },
  {
    path: ROUTES.NOTIFICATIONS,
    component: NotificationsSettingsPage,
    requiresAuth: true,
    title: generatePageTitle('Notifications'),
  },
  {
    path: ROUTES.PAYMENT_METHODS,
    component: PaymentMethodsPage,
    requiresAuth: true,
    title: generatePageTitle('Payment Methods'),
  },
  { path: ROUTES.SETTINGS, component: SettingsPage, requiresAuth: true, title: generatePageTitle('Settings') },
  { path: ROUTES.SECURITY, component: SecurityPage, requiresAuth: true, title: generatePageTitle('Security') },
  {
    // Requires auth: the account exists by the time anyone reaches the payment step. `App`'s gate
    // is what routes an unpaid member here; the route itself is reachable directly so a 3DS
    // redirect can come back to it.
    path: ROUTES.MEMBERSHIP_PAYMENT,
    component: MembershipPaymentPage,
    requiresAuth: true,
    title: generatePageTitle('Begin your membership'),
  },
  { path: ROUTES.ONBOARDING, component: OnboardingPage, requiresAuth: true, title: generatePageTitle('Get Started') },
  {
    // `App`'s gate is what routes a member without a verified phone here, and releases them after.
    path: ROUTES.VERIFY_PHONE,
    component: VerifyPhonePage,
    requiresAuth: true,
    title: generatePageTitle('Verify your phone number'),
  },
  {
    path: ROUTES.CHECKOUT_HOTEL,
    component: HotelCheckoutPage,
    requiresAuth: true,
    title: generatePageTitle('Checkout'),
  },
  {
    path: ROUTES.CHECKOUT_FLIGHT,
    component: FlightCheckoutPage,
    requiresAuth: true,
    title: generatePageTitle('Checkout'),
  },
  { path: ROUTES.LOGIN, component: LoginPage, requiresAuth: false, title: generatePageTitle('Sign in') },
  { path: ROUTES.SIGNUP, component: SignupPage, requiresAuth: false, title: generatePageTitle('Create your account') },
  {
    path: ROUTES.FORGOT_PASSWORD,
    component: ForgotPasswordPage,
    requiresAuth: false,
    title: generatePageTitle('Reset password'),
  },
  {
    path: ROUTES.RESET_PASSWORD,
    component: ResetPasswordPage,
    requiresAuth: false,
    title: generatePageTitle('Reset password'),
  },
  { path: ROUTES.FIREBASE_ACTION, component: FirebaseActionPage, requiresAuth: false, title: 'Elara' },
  { path: ROUTES.ADMIN, component: AdminShell, requiresAuth: true, title: generatePageTitle('Admin') },
  {
    // Public read-only itinerary behind a trip share link. Standalone: no login, payment, onboarding
    // or policy gate applies, for signed-out visitors and signed-in members alike.
    path: ROUTES.SHARED_ITINERARY,
    component: SharedItineraryPage,
    requiresAuth: false,
    standalone: true,
    title: generatePageTitle('Shared itinerary'),
  },
];

/**
 * Register default routes with the router state
 *
 * @param routerState - The router state instance
 */
export function registerDefaultRoutes(routerState: { registerRoute: (config: RouteConfig) => void }) {
  for (const routeConfig of DEFAULT_ROUTES) {
    routerState.registerRoute(routeConfig);
  }
}
